import { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';
import { DatePipe } from '@angular/common';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { Folder } from './folder.dto';
import { FolderService } from './folder.service';
import { environment } from 'src/environments/environment';

export class FolderDataSource implements DataSource<Folder> {
    private datePipe: DatePipe = new DatePipe('en-GB');
    private sort$ = new BehaviorSubject<Sort>(null);
    private filter$ = new BehaviorSubject<string>('');
    private deleted$ = new BehaviorSubject<boolean>(false);
    private page$ = new BehaviorSubject<PageEvent>({
        pageIndex: 0,
        pageSize: 100,
        length: 0,
    });

    constructor(
        private folderService: FolderService,
        private folder: Folder,
        private objectStore: string,
        private matPaginator: MatPaginator
    ) {}

    public sortChanged(input: Sort) {
        this.sort$.next(input);
    }

    public pageChange(input: PageEvent) {
        this.page$.next(input);
    }

    public filterChange(input: string) {
        this.filter$.next(input);
    }

    public deleteChanged(input: boolean) {
        this.deleted$.next(input);
    }

    connect(collectionViewer: CollectionViewer): Observable<Folder[]> {
        // console.log('Connect', this.folder);

        return combineLatest([
            of(this.folder),
            this.page$,
            this.sort$,
            this.filter$,
        ]).pipe(
            // tap(v => console.log('connect', v)),
            filter(
                ([folder, page]) =>
                    !!folder &&
                    folder.subFolders &&
                    folder.subFolders.length > 0
            ),
            switchMap(([folder, page, sort, filterValue]) =>
                // this.folderService.displayByIds(folder.subFolders).pipe(
                this.folderService
                    .getByIds(this.objectStore, folder.subFolders)
                    .pipe(
                        map(v => (v as unknown) as Folder[]),
                        map(results =>
                            filterValue && filterValue.length > 0
                                ? results.filter(row => {
                                      const doc = row as Folder;
                                      const keys = Object.keys(
                                          doc.propertiesObj
                                      );
                                      const matchedKeys = keys.filter(k => {
                                          const value = doc.properties.find(
                                              v => v.name === k
                                          );
                                          // TODO: We need all types here
                                          let matched = false;
                                          if (
                                              value.type ===
                                              'class java.util.Date'
                                          ) {
                                              const toDate = this.datePipe.transform(
                                                  value.value,
                                                  environment.dateFormat
                                              );
                                              matched =
                                                  toDate.indexOf(filterValue) >=
                                                  0;
                                          } else {
                                              matched =
                                                  JSON.stringify(value.value)
                                                      .toLowerCase()
                                                      .indexOf(filterValue) >=
                                                  0;
                                          }

                                          return matched;
                                      });
                                      return matchedKeys.length > 0;
                                  })
                                : results
                        ),
                        tap(
                            results =>
                                (this.matPaginator.length = results.length)
                        ),
                        map(results =>
                            sort
                                ? results.sort((a, b) => {
                                      let result = 0;
                                      const aObj = a as any;
                                      const bObj = b as any;
                                      const x = aObj.propertiesObj[sort.active];
                                      const y = bObj.propertiesObj[sort.active];
                                      if (x > y) {
                                          result = 1;
                                      } else if (x < y) {
                                          result = -1;
                                      } else if (x === undefined && y) {
                                          result = 1;
                                      } else if (x && y === undefined) {
                                          result = -1;
                                      }

                                      if (sort.direction === 'desc') {
                                          result = result * -1;
                                      }
                                      return result;
                                  })
                                : results
                        ),
                        map(results =>
                            page && page.pageSize
                                ? results.slice(
                                      page.pageIndex * page.pageSize,
                                      (page.pageIndex + 1) * page.pageSize
                                  )
                                : results
                        )
                        // tap(v => console.log('Returning', v))
                    )
            )
        );
    }

    disconnect(
        collectionViewer: import('@angular/cdk/collections').CollectionViewer
    ): void {}
}
